import { useHandler } from "@redotech/react-util/hook";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { StepType } from "@redotech/redo-model/advanced-flow/common";
import type {
  ConditionGroupV3,
  RuleV3ConditionsStep,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import type { RuleTriggerType } from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo } from "react";
import { ConditionGroupEditorV3 } from "../../../../rules/components/condition-group-editor-v3";
import { useDefaultRuleEditContext } from "../../../../rules/default-rule-edit-context";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  DeepPartial,
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";

function countConditionsV3Recursive(
  group: DeepPartial<ConditionGroupV3>,
): number {
  if (!group.conditions) {
    return 0;
  }
  return group.conditions.reduce((count, item) => {
    if (item && "operator" in item) {
      return count + countConditionsV3Recursive(item);
    }
    return count + 1;
  }, 0);
}

const RuleConditionsV3Inspector = memo(function RuleConditionsV3Inspector({
  step,
  steps,
  setStep,
  onClose,
  setHoveredNodeId,
  automation,
}: StepTypeDetailsProps<PartialWithRequiredType<RuleV3ConditionsStep>>) {
  const handleUpdateGroup = useHandler((updated: ConditionGroupV3) => {
    setStep({ ...step, conditionGroups: updated });
  });

  const triggerStep = automation?.steps?.find(
    (s) => s.type === StepType.TRIGGER,
  );
  const triggerType = (triggerStep as { key?: string } | undefined)?.key as
    | RuleTriggerType
    | undefined;

  const rootGroup = (step.conditionGroups || {
    operator: "AND",
    conditions: [],
  }) as ConditionGroupV3;

  const trueStep = step.nextTrueId
    ? steps.find((s) => s.id === step.nextTrueId)
    : undefined;
  const falseStep = step.nextFalseId
    ? steps.find((s) => s.id === step.nextFalseId)
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps, setStep, setHoveredNodeId);

  const handleTrueStepSelected = createConnectionHandler(0);
  const handleFalseStepSelected = createConnectionHandler(1);

  // Default-rule templates fix the flow's routing — lock the
  // Next Steps dropdowns when we're inside a default-rule edit page.
  const structureLocked = useDefaultRuleEditContext() !== null;

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          Rule Conditions
        </Text>
        {onClose && <RedoButton IconLeading={XCloseIcon} onClick={onClose} />}
      </Flex>

      <Text fontSize="sm" textColor="secondary">
        Add conditions and groups with AND/OR logic for flexible rule matching.
      </Text>

      <Flex dir="column" gap="lg">
        <ConditionGroupEditorV3
          group={rootGroup}
          onChange={handleUpdateGroup}
          triggerType={triggerType}
        />

        {(!rootGroup.conditions || rootGroup.conditions.length === 0) && (
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" textColor="tertiary">
              No conditions added yet. At least one condition is required.
            </Text>
          </Flex>
        )}
      </Flex>

      <Divider />

      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold">
          Next Steps
        </Text>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            disabled={structureLocked}
            fitToAnchor
            label="If conditions match (True)"
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              if (structureLocked) {
                return;
              }
              handleTrueStepSelected(item.value);
            }}
            placeholder="Select a step or choose Unassign"
            selectedItem={
              trueStep
                ? availableSteps.find((opt) => opt.value === trueStep.id)
                : undefined
            }
          />
        </Flex>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            disabled={structureLocked}
            fitToAnchor
            label="If conditions do not match (False)"
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              if (structureLocked) {
                return;
              }
              handleFalseStepSelected(item.value);
            }}
            placeholder="Select a step or choose Unassign"
            selectedItem={
              falseStep
                ? availableSteps.find((opt) => opt.value === falseStep.id)
                : undefined
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const ruleConditionsV3StepType: FrontendStepType<
  PartialWithRequiredType<RuleV3ConditionsStep>
> = {
  valid: (state) => {
    const hasConditions =
      state.conditionGroups !== undefined &&
      countConditionsV3Recursive(state.conditionGroups) > 0;
    const hasRouting =
      state.nextTrueId !== undefined || state.nextFalseId !== undefined;
    return hasConditions && hasRouting;
  },
  type: "custom",
  customInspector: RuleConditionsV3Inspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrueId = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalseId = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for Rule Conditions V3 step`,
        );
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextTrueId === targetStepId) {
        draft.nextTrueId = undefined;
      }
      if (draft.nextFalseId === targetStepId) {
        draft.nextFalseId = undefined;
      }
    });
  },
};
